<script>
  import { onMount, onDestroy } from 'svelte';
  import { navigate as goto } from 'svelte-routing';
  import { selectedStudy, selectedSite, selectedParticipant } from '$stores/selectionStore';
  import { studyProperties, isFeatureEnabled, fetchStudyProperties } from '$stores/configStore';
  import StudyHeader from '$lib/StudyHeader.svelte';
  import ParticipantDetails from '$lib/ParticipantDetails.svelte';
  import FeatureInfo from '$lib/FeatureInfo.svelte';
  import DailyCGMChart from './person/DailyCGMChart.svelte';
  import DailySMBGChart from './person/DailySMBGChart.svelte';
  import PersonInfoCards from '$lib/PersonInfoCards.svelte';
  import BaselineStatsCards from '$lib/BaselineStatsCards.svelte';
  import LatestStatsCards from '$lib/LatestStatsCards.svelte';
  import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
  import { faToggleLargeOn, faToggleLargeOff } from '@fortawesome/pro-light-svg-icons';
  import { faToggleOn, faToggleOff } from '@fortawesome/free-solid-svg-icons';
  import Toolbar from '$lib/Toolbar.svelte';
  import ThreeStateIndicator from '$lib/ThreeStateIndicator.svelte';
  import PopupForm from './persons/PersonForm.svelte';
import AuditLogModal from '$lib/AuditLogModal.svelte';
import SubjectFormProxyModal from '$lib/SubjectFormProxyModal.svelte';

  // Use local API endpoints instead of direct API with key
  const API_BASE_URL = '/api/proxy';

  import { formatToLocalDateTime, isWithinLastHours, isOlderThanHours } from '$utils/dateUtils.js';
  import { theme } from '$stores/themeStore';
  import { user, userRoles, userReadOnly } from '$stores/sessionStore';
  import { hasAuditLogAccess } from '$utils/permissionUtils.js';

  // Define view states
  const VIEW_STATES = {
    GENERAL: 'general',
    BASELINE: 'baseline',
    LATEST: 'latest',
  };

  let currentView = VIEW_STATES.GENERAL; // Default to general view

  let cgmCompliances = [];
  let smbgCompliances = [];

  let dailyCGMs = [];
  let dailyCGMChartVisible = true;
  let isLoadingdailyCGM = false;

  let complianceRange = 99999; // Keep this for fetching all data
  let displayRange = 28; // New variable for display range - will be updated from study properties
  let totalCGMReadings = 0;
  let totalValidCGMReadings = 0;
  let totalInvalidCGMReadings = 0;
  let totalSMBGReadings = 0;
  let uniqueSensors = new Set();
  let firstCGMReading = null;
  let firstSMBGReading = null;

  let dailySMBGs = [];
  let dailySMBGChartVisible = true;
  let isLoadingdailySMBG = false;

  // Add chart data variables
  let complianceChartData = {
    labels: [],
    datasets: [
      {
        label: 'CGM Readings',
        data: [],
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        fill: true,
      },
      {
        label: 'SMBG Readings',
        data: [],
        borderColor: 'rgb(16, 185, 129)',
        backgroundColor: 'rgba(16, 185, 129, 0.1)',
        fill: true,
      },
    ],
  };

  // Add loading states for compliances
  let isLoadingCGMCompliance = false;
  let isLoadingSMBGCompliance = false;

  // Add state for device activities (CGM sensors)
  let deviceActivities = [];
  let isLoadingDeviceActivities = false;

  // Add state for latest sensor details
  let latestSensorDetails = null;
  let latestCGMReadingDevice = null;
  let isLoadingSensorDetails = false;
  let latestSensorDetailsRetryAt = 0;
  const LATEST_SENSOR_DETAILS_RETRY_MS = 60000;

  // Store the result of hasActiveCGMSensor in a reactive variable
  let hasActiveSensor = false;

  let totalCGMs = 0;
  let totalSMBGs = 0;

  // Loading states for total days calculations
  let isLoadingTotalCGMDays = false;
  let isLoadingTotalSMBGDays = false;

  let cgmStats = [];

  let timeInterval;
  let localTime = '';
  // Offline CGM mode (reader upload) when interval is 15 minutes
  let isOfflineCGM = false;

  let showForm = false;
  let showAuditLog = false;
  let showSubjectFormProxyModal = false;
  let editParticipant = null;
  let auditLogParticipant = null;
  let refreshParticipantDetails = null;
  let participantDetailsKey = 0;
  let fullParticipantIdLoaded = null;
  let isHydratingParticipant = false;
  let hydratedParticipantIds = new Set();
  $: isParticipantHydrated = !!$selectedParticipant?.enrollmentDate || !!$selectedParticipant?.randomizationDate;

  // Feature flag for participant card view
  let isParticipantCardViewEnabled = true;

  // Reactive variable for feature flags
  $: {
    isParticipantCardViewEnabled = isFeatureEnabled('cardViews.participant');
  }

  // Add computed property for audit log permissions
  $: canViewAuditLogs = hasAuditLogAccess($userRoles, $studyProperties, $userReadOnly);

  // Subscribe to selectedStudy changes to fetch properties
  $: if ($selectedStudy?._id) {
    fetchStudyProperties($selectedStudy._id);
  } else {
    studyProperties.set(null);
  }

  // Function to update local time
  const updateLocalTime = () => {
    if ($selectedParticipant && $selectedParticipant.timezoneCode) {
      const options = {
        timeZone: $selectedParticipant.timezoneCode,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      };
      const formatter = new Intl.DateTimeFormat([], options);
      localTime = formatter.format(new Date());
    }
  };

  onMount(async () => {
    if ($selectedParticipant) {
      timeInterval = setInterval(updateLocalTime, 1000);
      updateLocalTime(); // Call immediately to set initial value
    }
  });
  // Fetch and normalize the full participant record
  async function fetchFullParticipant(participantId) {
    try {
      isHydratingParticipant = true;
      const res = await fetch(`${API_BASE_URL}/persons/${participantId}`);
      if (!res.ok) {
        return;
      }
      const data = await res.json();
      const full = data?.data || data;
      if (full && full._id) {
        // Only update the store if dates are missing or the payload differs materially
        const hasDates = !!$selectedParticipant?.enrollmentDate || !!$selectedParticipant?.randomizationDate;
        if (!hasDates || full._id !== $selectedParticipant?._id) {
          selectedParticipant.set(full);
        }
        fullParticipantIdLoaded = full._id;
      }
    } catch (e) {
      console.error('Error fetching full participant:', e);
    } finally {
      isHydratingParticipant = false;
      hydratedParticipantIds = new Set(hydratedParticipantIds);
      hydratedParticipantIds.add(participantId);
    }
  }

  // Ensure we have full participant details (including dates) on page load/change
  $: if ($selectedParticipant?._id) {
    const id = $selectedParticipant._id;
    const hasDates = !!$selectedParticipant.enrollmentDate || !!$selectedParticipant.randomizationDate;
    const hasHydrated = hydratedParticipantIds.has(id);
    if (!isHydratingParticipant && !hasHydrated && (fullParticipantIdLoaded !== id || !hasDates)) {
      fetchFullParticipant(id);
    }
  }

  onDestroy(() => {
    if (timeInterval) {
      clearInterval(timeInterval);
    }
  });

  // Fetch study properties and update display range
  async function fetchStudyPropertiesAndUpdateRange() {
    if ($selectedStudy?._id) {
      await fetchStudyProperties($selectedStudy._id);
    }
  }

  $: if ($selectedParticipant) {
    updateLocalTime(); // Update time when participant changes
  }

  async function fetchDailyCGMs() {
    isLoadingdailyCGM = true;

    try {
      const res = await fetch(
        `${API_BASE_URL}/glucoseReadings/dailyChart?page=1&pageSize=${displayRange}&subjectGUID=${$selectedParticipant._id}&type=CGM&timezone=${$selectedParticipant.timezoneCode || 'UTC'}`
      );

      if (!res.ok) {
        throw new Error('Failed to fetch cgm compliance');
      }

      const response = await res.json();
      const readings = response.data || [];
      dailyCGMs = readings;

      // Process CGM data for info cards
      // Note: totalCGMReadings and uniqueSensors are now calculated in fetchUniqueSensors()

      // Get first reading using the first array element
      firstCGMReading =
        readings.length > 0 ? { ...readings[0], timestamp: readings[0].minTimestamp } : null;

      // Note: totalCGMs is now calculated in fetchTotalCGMDays() to include all historical data

      updateComplianceChart();
      isLoadingdailyCGM = false;
    } catch (err) {
      console.error('Error fetching daily CGMs:', err);
      dailyCGMs = [];
      isLoadingdailyCGM = false;
    }
  }

  // Fetch total CGM days count (all historical data, not limited to displayRange)
  async function fetchTotalCGMDays() {
    isLoadingTotalCGMDays = true;

    try {
      const res = await fetch(
        `${API_BASE_URL}/glucoseReadings/dailyChart?page=1&pageSize=3650&subjectGUID=${$selectedParticipant._id}&type=CGM&timezone=${$selectedParticipant.timezoneCode || 'UTC'}`
      );

      if (!res.ok) {
        throw new Error('Failed to fetch total CGM days');
      }

      const response = await res.json();
      const readings = response.data || [];

      // Count days with CGM data (count > 0)
      totalCGMs = readings.filter((day) => day.count > 0).length;
    } catch (err) {
      console.error('Error fetching total CGM days:', err);
      totalCGMs = 0;
    } finally {
      isLoadingTotalCGMDays = false;
    }
  }

  async function fetchDailySMBGs() {
    isLoadingdailySMBG = true;

    try {
      const res = await fetch(
        `${API_BASE_URL}/glucoseReadings/dailyChart?page=1&pageSize=${displayRange}&subjectGUID=${$selectedParticipant._id}&type=SMBG&timezone=${$selectedParticipant.timezoneCode || 'UTC'}`
      );

      if (!res.ok) {
        throw new Error('Failed to fetch smbg compliance');
      }

      const response = await res.json();
      const readings = response.data || [];
      dailySMBGs = readings;

      // Note: totalSMBGs and totalSMBGReadings are now calculated in fetchTotalSMBGDays() to include all historical data

      // Get first reading using the first array element
      firstSMBGReading =
        readings.length > 0 ? { ...readings[0], timestamp: readings[0].minTimestamp } : null;

      updateComplianceChart();
      isLoadingdailySMBG = false;
    } catch (err) {
      console.error('Error fetching daily SMBGs:', err);
      dailySMBGs = [];
      isLoadingdailySMBG = false;
    }
  }

  // Fetch total SMBG days and readings count (all historical data, not limited to displayRange)
  async function fetchTotalSMBGDays() {
    isLoadingTotalSMBGDays = true;

    try {
      const res = await fetch(
        `${API_BASE_URL}/glucoseReadings/dailyChart?page=1&pageSize=3650&subjectGUID=${$selectedParticipant._id}&type=SMBG&timezone=${$selectedParticipant.timezoneCode || 'UTC'}`
      );

      if (!res.ok) {
        throw new Error('Failed to fetch total SMBG days');
      }

      const response = await res.json();
      const readings = response.data || [];

      // Count days with SMBG data (count > 0)
      totalSMBGs = readings.filter((day) => day.count > 0).length;

      // Calculate total SMBG readings across all days
      totalSMBGReadings = readings.reduce((sum, day) => sum + (day.count || 0), 0);
    } catch (err) {
      console.error('Error fetching total SMBG days:', err);
      totalSMBGs = 0;
      totalSMBGReadings = 0;
    } finally {
      isLoadingTotalSMBGDays = false;
    }
  }

  async function fetchCGMCompliances() {
    isLoadingCGMCompliance = true;

    try {
      const res = await fetch(
        `${API_BASE_URL}/glucoseReadings/daily?page=1&pageSize=1&subjectGUID=${$selectedParticipant._id}&type=CGM&timezone=${$selectedParticipant.timezoneCode || 'UTC'}`
      );

      if (!res.ok) {
        throw new Error('Failed to fetch cgm compliance');
      }

      const data = await res.json();
      cgmCompliances = data.data || [];
    } catch (err) {
      console.error('Error fetching CGM compliances:', err);
      cgmCompliances = [];
    } finally {
      isLoadingCGMCompliance = false;
    }
  }

  async function fetchSMBGCompliances() {
    isLoadingSMBGCompliance = true;

    try {
      const res = await fetch(
        `${API_BASE_URL}/glucoseReadings/daily?page=1&pageSize=1&subjectGUID=${$selectedParticipant._id}&type=SMBG&timezone=${$selectedParticipant.timezoneCode || 'UTC'}`
      );

      if (!res.ok) {
        throw new Error('Failed to fetch smbg compliance');
      }

      const data = await res.json();
      smbgCompliances = data.data || [];
    } catch (err) {
      console.error('Error fetching SMBG compliances:', err);
      smbgCompliances = [];
    } finally {
      isLoadingSMBGCompliance = false;
    }
  }

  // Fetch device activities to check for active CGM sensors
  async function fetchDeviceActivities() {
    isLoadingDeviceActivities = true;

    try {
      const res = await fetch(
        `${API_BASE_URL}/subjectSchedule/subjectSchedules?subjectGUID=${$selectedParticipant._id}`
      );

      if (!res.ok) {
        throw new Error('Failed to fetch subject activities');
      }

      const data = await res.json();
      deviceActivities = data || [];
    } catch (err) {
      console.error('Error fetching device activities:', err);
      deviceActivities = [];
    } finally {
      isLoadingDeviceActivities = false;
    }
  }

  // Fetch latest sensor details for active sensor
  async function fetchLatestSensorDetails() {
    if (!hasActiveSensor && !isOfflineCGM) {
      latestSensorDetails = null;
      return;
    }

    // Don't fetch if we're already loading or if we already have data
    if (isLoadingSensorDetails || latestSensorDetails) {
      return;
    }

    const now = Date.now();
    if (latestSensorDetailsRetryAt && now < latestSensorDetailsRetryAt) {
      return;
    }

    isLoadingSensorDetails = true;
    
    try {
      let devices = null;
      
      // OPTIMIZATION: Use cached data from fetchUniqueSensors if available
      if (cachedDeviceComplianceValid && cachedDeviceComplianceValid.length > 0) {
        devices = cachedDeviceComplianceValid;
      } else {
        // Fallback: fetch if cache not available
        const resCompliance = await fetch(
          `${API_BASE_URL}/glucoseReadings/deviceCompliance?page=1&pageSize=999999&subjectGUID=${$selectedParticipant._id}&inRange=true`
        );

        if (resCompliance.ok) {
          const compliance = await resCompliance.json();
          devices = compliance?.data || [];
        }
      }

      if (devices && devices.length > 0) {
        // Filter valid sensors and pick the most recent by maxRead
        const valid = devices.filter((d) => d && d.sensor && d.sensor !== 'null' && d.maxRead);
        if (valid.length > 0) {
          const latest = valid.reduce((a, b) => new Date(a.maxRead) >= new Date(b.maxRead) ? a : b);
          latestSensorDetails = {
            externalId: latest.sensor,
            sensorStartTimestamp: latest.minRead,
            sensorEndTimestamp: latest.maxRead,
            count: latest.count || 0
          };
        }
      }

      // Fallback: use daily aggregation if device compliance didn't yield details
      if (!latestSensorDetails) {
        const resDaily = await fetch(
          `${API_BASE_URL}/glucoseReadings/daily?page=1&pageSize=365&subjectGUID=${$selectedParticipant._id}&type=CGM&timezone=${$selectedParticipant.timezoneCode || 'UTC'}`
        );
        if (resDaily.ok) {
          const data = await resDaily.json();
          const allDays = data?.data || [];
          const currentDay = allDays.find((d) => {
            if (!d) return false;
            const dayCount = Number(d.totalCount ?? d.count ?? 0);
            return dayCount > 0 && d.externalId && d.externalId !== 'null';
          });
          if (currentDay) {
            const currentExternalId = currentDay.externalId;
            const currentLocation = currentDay.location;
            const sensorDays = allDays.filter((day) => {
              if (!day) return false;
              const dayCount = Number(day.totalCount ?? day.count ?? 0);
              return (
                dayCount > 0 &&
                day.externalId === currentExternalId &&
                day.location === currentLocation
              );
            });
            if (sensorDays.length > 0) {
              const latestDay = sensorDays.reduce((a, b) =>
                new Date(a.maxTimestamp) >= new Date(b.maxTimestamp) ? a : b
              );
              const earliestDay = sensorDays.reduce((a, b) =>
                new Date(a.minTimestamp) <= new Date(b.minTimestamp) ? a : b
              );
              const totalCountForSensor = sensorDays.reduce((sum, d) => sum + Number(d.totalCount ?? d.count ?? 0), 0);
              latestSensorDetails = {
                ...latestDay,
                externalId: currentExternalId,
                sensorStartTimestamp: earliestDay.minTimestamp,
                sensorEndTimestamp: latestDay.maxTimestamp,
                count: totalCountForSensor
              };
            }
          }
        }
      }
      if (!latestSensorDetails) {
        latestSensorDetails = null;
      }
    } catch (err) {
      console.error('Error fetching latest sensor details:', err);
      latestSensorDetails = null;
    } finally {
      if (!latestSensorDetails) {
        latestSensorDetailsRetryAt = Date.now() + LATEST_SENSOR_DETAILS_RETRY_MS;
      } else {
        latestSensorDetailsRetryAt = 0;
      }
      isLoadingSensorDetails = false;
    }
  }

  async function fetchLatestCGMReadingDevice() {
    if (!$selectedParticipant?._id) {
      latestCGMReadingDevice = null;
      return;
    }

    try {
      const res = await fetch(
        `${API_BASE_URL}/glucoseReadings?page=1&pageSize=1&subjectGUID=${$selectedParticipant._id}&type=CGM&sortField=read&sortOrder=desc`
      );

      if (!res.ok) {
        throw new Error('Failed to fetch latest CGM reading');
      }

      const data = await res.json();
      const latestReading = data?.data?.[0] || null;
      latestCGMReadingDevice = latestReading
        ? {
            manufacturer: latestReading.manufacturer,
            model: latestReading.model
          }
        : null;
    } catch (err) {
      console.error('Error fetching latest CGM reading device:', err);
      latestCGMReadingDevice = null;
    }
  }

  async function fetchCGMStats() {
    try {
      let url = `${API_BASE_URL}/stats/subjectCGMStats?page=1&pageSize=999999&eventDuration=3&subjectGUIDs=${JSON.stringify([$selectedParticipant._id])}`;

      if ($selectedStudy?._id) {
        url += `&studyGUID=${$selectedStudy._id}`;
      }
      const res = await fetch(url);

      if (!res.ok) {
        throw new Error('Failed to fetch CGM stats');
      }

      const responseData = await res.json();

      // Sort by firstRead to get the oldest sensor first
      cgmStats = responseData.data.sort((a, b) => new Date(a.firstRead) - new Date(b.firstRead));
    } catch (err) {
      console.error('Error fetching CGM stats:', err);
      cgmStats = [];
    }
  }

  // Cached device compliance data to avoid redundant API calls
  let cachedDeviceComplianceValid = null;

  // Fetch unique sensors using device compliance endpoint
  async function fetchUniqueSensors() {
    isLoadingUniqueSensors = true;

    try {
      // OPTIMIZATION: Run both calls in parallel instead of sequential
      const [resAll, resValid] = await Promise.all([
        fetch(`${API_BASE_URL}/glucoseReadings/deviceCompliance?page=1&pageSize=999999&subjectGUID=${$selectedParticipant._id}&type=CGM&inRange=false`),
        fetch(`${API_BASE_URL}/glucoseReadings/deviceCompliance?page=1&pageSize=999999&subjectGUID=${$selectedParticipant._id}&type=CGM&inRange=true`)
      ]);

      if (!resAll.ok || !resValid.ok) {
        throw new Error('Failed to fetch device compliance');
      }

      const [responseAll, responseValid] = await Promise.all([
        resAll.json(),
        resValid.json()
      ]);
      
      const deviceDataAll = responseAll.data || [];
      const deviceDataValid = responseValid.data || [];

      // Cache the valid data for fetchLatestSensorDetails to use
      cachedDeviceComplianceValid = deviceDataValid;
      
      // Extract unique sensors and calculate total readings from all device compliance data
      uniqueSensors = new Set();
      totalCGMReadings = 0;
      totalValidCGMReadings = 0;
      
      deviceDataAll.forEach((device) => {
        if (device.sensor && device.sensor !== 'null') {
          uniqueSensors.add(device.sensor);
        }
        // Add to total readings count (all readings)
        totalCGMReadings += device.count || 0;
      });
      
      deviceDataValid.forEach((device) => {
        // Add to valid readings count
        totalValidCGMReadings += device.count || 0;
      });
      
      // Calculate invalid readings as difference
      totalInvalidCGMReadings = totalCGMReadings - totalValidCGMReadings;
      
      // OPTIMIZATION: Also populate latestSensorDetails directly from the valid data
      // This eliminates the need for a separate API call in fetchLatestSensorDetails
      if (deviceDataValid.length > 0 && (hasActiveSensor || isOfflineCGM)) {
        const valid = deviceDataValid.filter((d) => d && d.sensor && d.sensor !== 'null' && d.maxRead);
        if (valid.length > 0) {
          const latest = valid.reduce((a, b) => new Date(a.maxRead) >= new Date(b.maxRead) ? a : b);
          latestSensorDetails = {
            externalId: latest.sensor,
            sensorStartTimestamp: latest.minRead,
            sensorEndTimestamp: latest.maxRead,
            count: latest.count || 0
          };
        }
      }
    } catch (err) {
      const duration = (performance.now() - startTime).toFixed(0);
      console.error(`[API] fetchUniqueSensors - ERROR in ${duration}ms:`, err);
      uniqueSensors = new Set();
      totalCGMReadings = 0;
      totalValidCGMReadings = 0;
      totalInvalidCGMReadings = 0;
      cachedDeviceComplianceValid = null;
    } finally {
      isLoadingUniqueSensors = false;
    }
  }

  // Feature flag variables
  let isSMBGEnabled = true;

  // Reactive variables for feature flags
  $: {
    isSMBGEnabled = isFeatureEnabled('smbg');
  }

  $: if ($selectedParticipant && isParticipantHydrated) {
    // Reset sensor details and cache when participant changes
    latestSensorDetails = null;
    latestCGMReadingDevice = null;
    hasActiveSensor = false;
    cachedDeviceComplianceValid = null;
    isLoadingUniqueSensors = false;
    latestSensorDetailsRetryAt = 0;
    
    fetchStudyPropertiesAndUpdateRange();
    fetchDailyCGMs();
    fetchTotalCGMDays();
    fetchCGMCompliances();
    fetchDeviceActivities();
    fetchUniqueSensors();
    fetchLatestCGMReadingDevice();

    if (isSMBGEnabled) {
      fetchDailySMBGs();
      fetchTotalSMBGDays();
      fetchSMBGCompliances();
    }

    // fetchCGMStats();
  }

  // Update display range when study properties are loaded
  $: if ($studyProperties?.glucoseParameters?.cgmCompliance?.complianceRange) {
    displayRange = $studyProperties.glucoseParameters.cgmCompliance.complianceRange;
  }
  // Derive offline mode from study settings
  $: isOfflineCGM = (($studyProperties?.glucoseParameters?.cgmIntervalMinutes ?? 5) === 15);

  // Utility function to calculate the expected count of readings
  function calculateExpectedCount(minTimestamp, maxTimestamp) {
    const start = new Date(minTimestamp);
    const end = new Date(maxTimestamp);

    // Calculate the difference in milliseconds and convert to minutes
    const diffMs = end - start;
    const diffMinutes = diffMs / (1000 * 60);

    const interval = $studyProperties?.glucoseParameters?.cgmIntervalMinutes ?? 5;
    return Math.floor(diffMinutes / interval);
  }

  /**
   * Calculates the compliance percentage.
   *
   * @param {Object} compliance - The compliance object.
   * @returns {string} - The compliance percentage formatted to two decimal places, or 'N/A' if not applicable.
   */
  function calculateCompliancePercentage(compliance) {
    if (compliance && compliance.minTimestamp && compliance.maxTimestamp) {
      const expected = calculateExpectedCount(compliance.minTimestamp, compliance.maxTimestamp);
      if (expected > 0) {
        const percentage = (compliance.count / expected) * 100;
        return `${Math.min(percentage, 100).toFixed(0)}%`;
      }
    }
    return 'N/A';
  }

  function calculateSensorProgress(minTimestamp, maxTimestamp, readingDevice) {
    if (!minTimestamp || !maxTimestamp) return { percentage: 0, daysLeft: 0, hoursLeft: 0 };

    const sensorType = readingDevice.manufacturer || '';
    // Prefer study setting if provided
    const SENSOR_LIFETIME_DAYS = $studyProperties?.glucoseParameters?.cgmDeviceDurationDays ?? 10;
    const firstReading = new Date(minTimestamp);
    const lastReading = new Date(maxTimestamp);



    // Calculate elapsed time in days from first reading to last reading
    const elapsedDays = (lastReading - firstReading) / (1000 * 60 * 60 * 24);

    // If we're beyond sensor lifetime, return 0 days left
    if (elapsedDays >= SENSOR_LIFETIME_DAYS) {
      return {
        percentage: 100,
        daysLeft: 0,
        hoursLeft: 0,
        lastReading,
        firstReading,
        sensorType,
        sensorLifetime: SENSOR_LIFETIME_DAYS,
        canComplete: false,
      };
    }

    const daysLeft = SENSOR_LIFETIME_DAYS - elapsedDays;
    const hoursLeft = Math.floor((daysLeft % 1) * 24);
    const percentage = Math.min(100, Math.round((elapsedDays / SENSOR_LIFETIME_DAYS) * 100));

    // Check if there's enough time left in our data range to complete the sensor lifetime
    const lastDayInRange = new Date(dailyCGMs[0].day);
    const timeLeftInRange = (lastDayInRange - lastReading) / (1000 * 60 * 60 * 24);
    const canComplete = timeLeftInRange >= daysLeft;

    return {
      percentage,
      daysLeft: Math.floor(daysLeft),
      hoursLeft,
      lastReading,
      firstReading,
      sensorType,
      sensorLifetime: SENSOR_LIFETIME_DAYS,
      canComplete,
    };
  }

  // Helper: get device duration days for a device name from study settings
  function getDeviceDurationForName(deviceName) {
    try {
      const arr = $studyProperties?.glucoseParameters?.deviceDurations || [];
      const found = arr.find((d) => String(d?.key || '').toLowerCase() === String(deviceName || '').toLowerCase());
      return found?.durationDays;
    } catch (_) {
      return undefined;
    }
  }

  // Offline progress based on count vs expected over full device duration
  function calculateOfflineProgress(details) {
    const interval = $studyProperties?.glucoseParameters?.cgmIntervalMinutes ?? 5;
    const perDayExpected = Math.round((24 * 60) / Math.max(interval, 1));
    const configuredDuration = getDeviceDurationForName('FreeStyle Libre Pro') ?? ($studyProperties?.glucoseParameters?.cgmDeviceDurationDays ?? 14);
    const expectedTotal = Math.max(perDayExpected * configuredDuration, 1);
    const actual = Number(details?.count || 0);
    const percentage = Math.min(100, Math.round((actual / expectedTotal) * 100));
    return { percentage, expectedTotal, perDayExpected, durationDays: configuredDuration };
  }

  // Check if there's an active CGM sensor in "Started" state
  function hasActiveCGMSensor() {
    if (!deviceActivities || deviceActivities.length === 0) {
      return false;
    }

    // Filter for CGM device activities that are in "Started" state
    const activeSensors = deviceActivities.filter((activity) => {
      // Check for CGM-related activity names
      const isCGMActivity = activity.typeCode === 'MEAS';

      // Check if status is "Started"
      const isStarted = activity.statusCode === 'START';

      return isCGMActivity && isStarted;
    });

    // if (activeSensors.length > 0) {
    //   console.log('Active sensor details:', activeSensors[0]);
    // }

    return activeSensors.length > 0;
  }

  // When any data loading completes, check again for active sensors
  $: if (!isLoadingDeviceActivities && deviceActivities && deviceActivities.length > 0) {
    setTimeout(() => {
      const newHasActiveSensor = hasActiveCGMSensor();
      // Only update if the value actually changed to prevent unnecessary re-renders
      if (hasActiveSensor !== newHasActiveSensor) {
        hasActiveSensor = newHasActiveSensor;
        //console.log('Updated active sensor status after loading:', hasActiveSensor);
      }
    }, 0);
  }

  // Track if fetchUniqueSensors is running to prevent redundant calls
  let isLoadingUniqueSensors = false;

  // Fetch sensor details when active sensor status changes
  // But skip if fetchUniqueSensors is already running (it will populate latestSensorDetails)
  $: if ((hasActiveSensor || isOfflineCGM) && !isLoadingSensorDetails && !latestSensorDetails && !isLoadingUniqueSensors) {
    fetchLatestSensorDetails();
  }

  // Create derived stores for loading states
  $: isLoadingCGM = isLoadingdailyCGM || isLoadingCGMCompliance || isLoadingDeviceActivities;
  $: isLoadingSMBG = isLoadingdailySMBG || isLoadingSMBGCompliance;

  $: themeKey = $theme;

  function updateComplianceChart() {
    if (!dailyCGMs.length || !dailySMBGs.length) {
      complianceChartData = {
        labels: [],
        datasets: [
          {
            label: 'CGM Readings',
            data: [],
            borderColor: 'rgb(59, 130, 246)',
            backgroundColor: 'rgba(59, 130, 246, 0.1)',
            fill: true,
          },
          {
            label: 'SMBG Readings',
            data: [],
            borderColor: 'rgb(16, 185, 129)',
            backgroundColor: 'rgba(16, 185, 129, 0.1)',
            fill: true,
          },
        ],
      };
      return;
    }

    // Only use the first displayRange days for the chart
    const displayCGMs = dailyCGMs.slice(0, displayRange);
    const displaySMBGs = dailySMBGs.slice(0, displayRange);

    // Update chart data
    complianceChartData = {
      labels: displayCGMs.map((day) => day.date),
      datasets: [
        {
          label: 'CGM Readings',
          data: displayCGMs.map((day) => day.count),
          borderColor: 'rgb(59, 130, 246)',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          fill: true,
        },
        {
          label: 'SMBG Readings',
          data: displaySMBGs.map((day) => day.count),
          borderColor: 'rgb(16, 185, 129)',
          backgroundColor: 'rgba(16, 185, 129, 0.1)',
          fill: true,
        },
      ],
    };
  }

  // Update the range change handler
  function handleRangeChange(event) {
    displayRange = parseInt(event.target.value);
    updateComplianceChart();
  }

  function toggleView() {
    // Cycle through views
    switch (currentView) {
      case VIEW_STATES.GENERAL:
        currentView = VIEW_STATES.BASELINE;
        break;
      case VIEW_STATES.BASELINE:
        currentView = VIEW_STATES.LATEST;
        break;
      case VIEW_STATES.LATEST:
        currentView = VIEW_STATES.GENERAL;
        break;
    }
  }

  // Function to get the numeric state for the indicator
  function getViewState() {
    switch (currentView) {
      case VIEW_STATES.GENERAL:
        return 0;
      case VIEW_STATES.BASELINE:
        return 1;
      case VIEW_STATES.LATEST:
        return 2;
      default:
        return 0;
    }
  }

  // Make the title reactive
  $: viewTitle = (() => {
    switch (currentView) {
      case VIEW_STATES.GENERAL:
        return 'General Metrics';
      case VIEW_STATES.BASELINE:
        return 'Baseline CGM Metrics';
      case VIEW_STATES.LATEST:
        return 'Latest CGM Metrics';
      default:
        return 'Statistics';
    }
  })();

  async function handleRefresh() {
    if ($selectedParticipant) {
      await Promise.all([
        fetchDailyCGMs(),
        fetchTotalCGMDays(),
        fetchDailySMBGs(),
        fetchTotalSMBGDays(),
        fetchCGMCompliances(),
        fetchSMBGCompliances(),
        fetchDeviceActivities(),
        fetchCGMStats(),
        fetchUniqueSensors(),
      ]);
    }
  }

  async function handleEditPerson(event) {
    const updatedPerson = event.detail;
    try {
      const res = await fetch(`${API_BASE_URL}/persons/${updatedPerson._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updatedPerson),
      });

      if (!res.ok) {
        throw new Error('Failed to update person');
      }

      const response = await res.json();
      
      // If the API doesn't return the updated data, fetch it separately
      if (response.data) {
        selectedParticipant.set(response.data);
      } else {
        const fetchRes = await fetch(`${API_BASE_URL}/persons/${updatedPerson._id}`);
        if (fetchRes.ok) {
          const fetchResponse = await fetchRes.json();
          if (fetchResponse.data) {
            selectedParticipant.set(fetchResponse.data);
          } else if (fetchResponse._id) {
            // API returns the participant object directly
            selectedParticipant.set(fetchResponse);
          }
        }
      }
      
      // Force ParticipantDetails to re-render by changing the key
      participantDetailsKey++;
      showForm = false;
      editParticipant = null;
    } catch (err) {
      console.error('Error updating person:', err);
    }
  }

  // Helper function to format timestamps in a shorter format
  function formatShortDateTime(timestamp, timezone) {
    if (!timestamp) return 'Unknown';
    try {
      const date = new Date(timestamp);
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone || 'UTC',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
      return formatter.format(date);
    } catch (_) {
      return 'Unknown';
    }
  }
</script>

<StudyHeader />

<FeatureInfo
  title="Participant Details"
  description="This page shows detailed information about the selected participant, including their personal information, device settings, and glucose readings."
  marginBottom="mb-0"
/>

{#if $selectedParticipant}
  {#key participantDetailsKey}
    <ParticipantDetails
      showOptionsMenu={true}
      {canViewAuditLogs}
      on:edit={async (e) => {
        console.log('Edit participant event received:', e.detail);
        try {
          const res = await fetch(`${API_BASE_URL}/persons/${e.detail?._id}`);
          if (res.ok) {
            const full = await res.json();
            editParticipant = full?.data || full;
          } else {
            editParticipant = e.detail;
          }
        } catch (_) {
          editParticipant = e.detail;
        } finally {
          showForm = true;
        }
      }}
      on:auditLog={(e) => {
        console.log('Participant audit log event received:', e.detail);
        auditLogParticipant = e.detail;
        showAuditLog = true;
      }}
      on:subjectFormProxies={(e) => {
        console.log('Subject form proxies event received:', e.detail);
        showSubjectFormProxyModal = true;
      }}
    />
  {/key}
{/if}

<!-- Title and Filters -->
<!-- <Toolbar title={viewTitle}>
  <div class="text-sm flex items-center gap-4">
    <button 
      on:click={toggleView} 
      class="pagination mx-1 p-0 flex items-center gap-2" 
      title={`Switch to ${currentView === VIEW_STATES.GENERAL ? 'Baseline Metrics' : currentView === VIEW_STATES.BASELINE ? 'Latest Metrics' : 'General Metrics'}`}
    >
      {#key currentView}
        <ThreeStateIndicator currentState={getViewState()} size={22} />
      {/key}
    </button>
  </div>
</Toolbar> -->

<!-- Add Infographic Cards -->
{#if isParticipantCardViewEnabled}
  <div class="mx-0 mt-2 print:scale-90 print:origin-top-left">
    {#if currentView === VIEW_STATES.GENERAL}
      <PersonInfoCards
        {uniqueSensors}
        {totalCGMs}
        {totalValidCGMReadings}
        {totalInvalidCGMReadings}
        {totalSMBGs}
        {totalSMBGReadings}
        isLoadingSensors={isLoadingUniqueSensors}
        isLoadingCGMDays={isLoadingTotalCGMDays}
        isLoadingCGMReadings={isLoadingUniqueSensors}
        isLoadingSMBGDays={isLoadingTotalSMBGDays}
        isLoadingSMBGReadings={isLoadingTotalSMBGDays}
      />
    {:else if currentView === VIEW_STATES.BASELINE}
      <BaselineStatsCards stats={cgmStats} studyProperties={$studyProperties} />
    {:else}
      <LatestStatsCards stats={cgmStats} studyProperties={$studyProperties} />
    {/if}
  </div>
{/if}

<div class="mx-1 flex gap-2 print:scale-90 print:origin-top-left">
  {#if cgmCompliances[0] && cgmCompliances.length > 0}
    <div class="pt-3 flex-1 w-1/2">
      <div
        class="bg-stone-200/50 dark:bg-gray-800/90 p-4 rounded-lg shadow-md w-full overflow-y-auto"
      >
        <div class="flex gap-3">
          <div class="flex-1">
            <div class="text-sky-800 dark:text-sky-600 text-l">
              {#if isOfflineCGM}
                <h2>Latest Sensor Info</h2>
                <div class="text-sm mb-2 text-gray-600 dark:text-gray-300 opacity-70">
                  Offline reader CGM summary
                </div>
              {:else}
                <h2>CGM Sensor Progress</h2>
                <div class="text-sm mb-2 text-gray-600 dark:text-gray-300 opacity-70">
                  The most recent cgm sensor
                </div>
              {/if}
            </div>
          </div>
        </div>

        <div class="flex gap-3">
          <div class="flex-1">
            <div class="text-sky-800 dark:text-sky-600 text-l">
              {#if isOfflineCGM}
                {#if dailyCGMs.length > 0 && latestSensorDetails}
                  {@const offline = calculateOfflineProgress(latestSensorDetails)}
                  <div class="flex items-center gap-4">
                    <div class="relative w-24 h-24 flex-shrink-0">
                      <svg class="w-full h-full" viewBox="0 0 36 36">
                        <path
                          d="M18 2.0845
                            a 15.9155 15.9155 0 0 1 0 31.831
                            a 15.9155 15.9155 0 0 1 0 -31.831"
                          fill="none"
                          stroke="#E9E9E9"
                          opacity="0.75"
                          stroke-width="2"
                          transform="rotate(90 18 18)"
                        />
                        <path
                          d="M18 2.0845
                            a 15.9155 15.9155 0 0 1 0 31.831
                            a 15.9155 15.9155 0 0 1 0 -31.831"
                          fill="none"
                          stroke="#4CAF50"
                          stroke-width="2"
                          stroke-dasharray="{offline?.percentage || 0}, 100"
                          transform="rotate(90 18 18)"
                        />
                        <text
                          x="18"
                          y="19"
                          class="percentage dark:fill-white fill-black opacity-50"
                          text-anchor="middle"
                          dominant-baseline="middle"
                          style="font-size: 9px; font-weight: bold;"
                        >
                          {offline?.percentage || 0}%
                        </text>
                      </svg>
                    </div>
                    <div class="flex-1 min-w-0">
                      <div class="text-xl font-semibold whitespace-nowrap truncate">
                        FreeStyle Libre Pro | {offline?.durationDays || 'Unknown'} Days ({latestSensorDetails?.externalId || 'Unknown'})
                      </div>
                      <div class="text-sm text-gray-600 dark:text-gray-100 whitespace-nowrap truncate">
                        {latestSensorDetails?.count || 0} of {offline?.expectedTotal || 0} readings
                      </div>
                      <div class="text-xs text-gray-600 dark:text-gray-300">
                        <div class="whitespace-nowrap truncate">
                          First valid reading: {latestSensorDetails?.sensorStartTimestamp
                            ? formatShortDateTime(
                                latestSensorDetails.sensorStartTimestamp,
                                $selectedParticipant.timezoneCode
                              )
                            : 'Unknown'}
                        </div>
                        <div class="whitespace-nowrap truncate">
                          Last valid reading: {#if latestSensorDetails?.sensorEndTimestamp}
                            <div class="inline-flex items-center gap-1">
                              <div
                                class={`w-1.5 h-1.5 rounded-full border flex-shrink-0 ${
                                  isOlderThanHours(latestSensorDetails.sensorEndTimestamp, 24)
                                    ? 'border-gray-400'
                                    : isOlderThanHours(latestSensorDetails.sensorEndTimestamp, 3)
                                      ? 'bg-orange-500 border-orange-500'
                                      : 'bg-green-500 border-green-500'
                                }`}
                              ></div>
                              {formatShortDateTime(
                                latestSensorDetails.sensorEndTimestamp,
                                $selectedParticipant.timezoneCode
                              )}
                            </div>
                          {:else}
                            Unknown
                          {/if}
                        </div>
                      </div>
                    </div>
                  </div>
                {:else}
                  {#if isLoadingSensorDetails || isLoadingdailyCGM || isLoadingUniqueSensors}
                    <div class="flex items-center gap-4">
                      <div class="relative w-24 h-24">
                        <svg class="w-full h-full" viewBox="0 0 36 36">
                          <path
                            d="M18 2.0845
                              a 15.9155 15.9155 0 0 1 0 31.831
                              a 15.9155 15.9155 0 0 1 0 -31.831"
                            fill="none"
                            stroke="#E9E9E9"
                            opacity="0.75"
                            stroke-width="2"
                            transform="rotate(90 18 18)"
                          />
                          <path
                            d="M18 2.0845
                              a 15.9155 15.9155 0 0 1 0 31.831
                              a 15.9155 15.9155 0 0 1 0 -31.831"
                            fill="none"
                            stroke="#4CAF50"
                            opacity="0.5"
                            stroke-width="2"
                            stroke-dasharray="25, 100"
                            transform="rotate(90 18 18)"
                          />
                          <text
                            x="18"
                            y="19"
                            class="percentage dark:fill-white fill-black opacity-50"
                            text-anchor="middle"
                            dominant-baseline="middle"
                            style="font-size: 9px; font-weight: bold;"
                          >
                            ...
                          </text>
                        </svg>
                      </div>
                      <div class="flex-1">
                        <div class="text-xl font-semibold text-gray-600 dark:text-gray-300">
                          Calculating Latest Sensor Info
                        </div>
                        <div class="text-sm text-gray-600 dark:text-gray-300">
                          Gathering recent sensor readings from offline uploads.
                        </div>
                      </div>
                    </div>
                  {:else}
                    <div class="flex items-center gap-4">
                      <div class="relative w-24 h-24">
                        <svg class="w-full h-full" viewBox="0 0 36 36">
                          <path
                            d="M18 2.0845
                              a 15.9155 15.9155 0 0 1 0 31.831
                              a 15.9155 15.9155 0 0 1 0 -31.831"
                            fill="none"
                            stroke="#E9E9E9"
                            opacity="0.75"
                            stroke-width="2"
                            transform="rotate(90 18 18)"
                          />
                          <path
                            d="M18 2.0845
                              a 15.9155 15.9155 0 0 1 0 31.831
                              a 15.9155 15.9155 0 0 1 0 -31.831"
                            fill="none"
                            stroke="#777777"
                            opacity="0.5"
                            stroke-width="2"
                            stroke-dasharray="0, 100"
                            transform="rotate(90 18 18)"
                          />
                          <text
                            x="18"
                            y="19"
                            class="percentage dark:fill-white fill-black opacity-50"
                            text-anchor="middle"
                            dominant-baseline="middle"
                            style="font-size: 9px; font-weight: bold;"
                          >
                            0%
                          </text>
                        </svg>
                      </div>
                      <div class="flex-1">
                        <div class="text-xl font-semibold text-gray-600 dark:text-gray-300">
                          No Sensor Data
                        </div>
                        <div class="text-sm text-gray-600 dark:text-gray-300">
                          No offline CGM readings found for this participant.
                        </div>
                      </div>
                    </div>
                  {/if}
                {/if}
              {:else}
                {#if dailyCGMs.length > 0 && hasActiveSensor && latestSensorDetails}
                  {@const status = calculateSensorProgress(
                    latestSensorDetails?.sensorStartTimestamp,
                    latestSensorDetails?.sensorEndTimestamp,
                    latestCGMReadingDevice
                  )}
                  <div class="flex items-center gap-4">
                    <div class="relative w-24 h-24 flex-shrink-0">
                      <svg class="w-full h-full" viewBox="0 0 36 36">
                        <path
                          d="M18 2.0845
                            a 15.9155 15.9155 0 0 1 0 31.831
                            a 15.9155 15.9155 0 0 1 0 -31.831"
                          fill="none"
                          stroke="#E9E9E9"
                          opacity="0.75"
                          stroke-width="2"
                          transform="rotate(90 18 18)"
                        />
                        <path
                          d="M18 2.0845
                            a 15.9155 15.9155 0 0 1 0 31.831
                            a 15.9155 15.9155 0 0 1 0 -31.831"
                          fill="none"
                          stroke={status?.canComplete ? '#797979' : '#4CAF50'}
                          stroke-width="2"
                          stroke-dasharray="{status?.percentage || 0}, 100"
                          transform="rotate(90 18 18)"
                        />
                        <text
                          x="18"
                          y="19"
                          class="percentage dark:fill-white fill-black opacity-50"
                          text-anchor="middle"
                          dominant-baseline="middle"
                          style="font-size: 9px; font-weight: bold;"
                        >
                          {#if status?.percentage}
                            {status.percentage}%
                          {:else}
                            0%
                          {/if}
                        </text>
                      </svg>
                    </div>
                    {#if status && latestSensorDetails}
                      <div class="flex-1 min-w-0">
                        <div class="text-xl font-semibold whitespace-nowrap truncate">
                          {#if status?.sensorType}
                            {#if status.sensorType.includes('FreeStyle Libre 3 Plus')}
                              Libre 3 Plus
                            {:else if status.sensorType.includes('FreeStyle Libre 3')}
                              Libre 3
                            {:else if status.sensorType.includes('FreeStyle Libre 2')}
                              Libre 2
                            {:else if status.sensorType.includes('Dexcom G7')}
                              G7
                            {:else if status.sensorType.includes('Dexcom G6')}
                              G6
                            {:else}
                              {status.sensorType}
                            {/if}
                          {:else}
                            Unknown
                          {/if}
                          | {status?.sensorLifetime || 'Unknown'} Days ({latestSensorDetails?.externalId
                            ? latestSensorDetails.externalId
                            : 'Unknown'})
                        </div>
                        <div class="text-sm text-gray-600 dark:text-gray-100 whitespace-nowrap truncate">
                          {status?.daysLeft || 0}
                          {status?.daysLeft === 1 ? 'Day' : 'Days'} and {status?.hoursLeft || 0}
                          {status?.hoursLeft === 1 ? 'hour' : 'hours'} left
                        </div>
                        <div class="text-xs text-gray-600 dark:text-gray-300">
                          <div class="whitespace-nowrap truncate">
                            First valid reading: {status?.firstReading
                              ? formatShortDateTime(
                                  status.firstReading,
                                  $selectedParticipant.timezoneCode
                                )
                              : 'Unknown'}
                          </div>
                          <div class="whitespace-nowrap truncate">
                            Last valid reading: {#if status?.lastReading}
                              <div class="inline-flex items-center gap-1">
                                <div
                                  class={`w-1.5 h-1.5 rounded-full border flex-shrink-0 ${
                                    isOlderThanHours(status.lastReading, 24)
                                      ? 'border-gray-400'
                                      : isOlderThanHours(status.lastReading, 3)
                                        ? 'bg-orange-500 border-orange-500'
                                        : 'bg-green-500 border-green-500'
                                  }`}
                                ></div>
                                {formatShortDateTime(
                                  status.lastReading,
                                  $selectedParticipant.timezoneCode
                                )}
                              </div>
                            {:else}
                              Unknown
                            {/if}
                          </div>
                        </div>
                      </div>
                    {:else}
                      <div class="flex-1">
                        <div class="text-sm text-gray-600 dark:text-gray-300">
                          No active sensor found in the selected date range
                        </div>
                      </div>
                    {/if}
                  </div>
                {:else}
                  {#if isLoadingDeviceActivities || isLoadingSensorDetails || isLoadingdailyCGM || isLoadingUniqueSensors || (hasActiveSensor && !latestSensorDetails)}
                    <div class="flex items-center gap-4">
                      <div class="relative w-24 h-24">
                        <svg class="w-full h-full" viewBox="0 0 36 36">
                          <path
                            d="M18 2.0845
                              a 15.9155 15.9155 0 0 1 0 31.831
                              a 15.9155 15.9155 0 0 1 0 -31.831"
                            fill="none"
                            stroke="#E9E9E9"
                            opacity="0.75"
                            stroke-width="2"
                            transform="rotate(90 18 18)"
                          />
                          <path
                            d="M18 2.0845
                              a 15.9155 15.9155 0 0 1 0 31.831
                              a 15.9155 15.9155 0 0 1 0 -31.831"
                            fill="none"
                            stroke="#4CAF50"
                            opacity="0.5"
                            stroke-width="2"
                            stroke-dasharray="25, 100"
                            transform="rotate(90 18 18)"
                          />
                          <text
                            x="18"
                            y="19"
                            class="percentage dark:fill-white fill-black opacity-50"
                            text-anchor="middle"
                            dominant-baseline="middle"
                            style="font-size: 9px; font-weight: bold;"
                          >
                            ...
                          </text>
                        </svg>
                      </div>
                      <div class="flex-1">
                        <div class="text-xl font-semibold text-gray-600 dark:text-gray-300">
                          Calculating Sensor Progress
                        </div>
                        <div class="text-sm text-gray-600 dark:text-gray-300">
                          Checking for an active CGM sensor and recent readings.
                        </div>
                      </div>
                    </div>
                  {:else}
                    <div class="flex items-center gap-4">
                      <div class="relative w-24 h-24">
                        <svg class="w-full h-full" viewBox="0 0 36 36">
                          <path
                            d="M18 2.0845
                              a 15.9155 15.9155 0 0 1 0 31.831
                              a 15.9155 15.9155 0 0 1 0 -31.831"
                            fill="none"
                            stroke="#E9E9E9"
                            opacity="0.75"
                            stroke-width="2"
                            transform="rotate(90 18 18)"
                          />
                          <path
                            d="M18 2.0845
                              a 15.9155 15.9155 0 0 1 0 31.831
                              a 15.9155 15.9155 0 0 1 0 -31.831"
                            fill="none"
                            stroke="#777777"
                            opacity="0.5"
                            stroke-width="2"
                            stroke-dasharray="0, 100"
                            transform="rotate(90 18 18)"
                          />
                          <text
                            x="18"
                            y="19"
                            class="percentage dark:fill-white fill-black opacity-50"
                            text-anchor="middle"
                            dominant-baseline="middle"
                            style="font-size: 9px; font-weight: bold;"
                          >
                            0%
                          </text>
                        </svg>
                      </div>
                      <div class="flex-1">
                        <div class="text-xl font-semibold text-gray-600 dark:text-gray-300">
                          No Active CGM Sensor
                        </div>
                        <div class="text-sm text-gray-600 dark:text-gray-300">
                          The participant needs to start a CGM sensor to collect data.
                        </div>
                        <div class="text-xs text-gray-600 dark:text-gray-300">
                          Check the Schedule page for sensor activity.
                        </div>
                      </div>
                    </div>
                  {/if}
                {/if}
              {/if}
            </div>
          </div>
        </div>
      </div>
    </div>
  {:else}
    <div class="pt-3 flex-1 w-1/2">
      <div
        class="h-full flex text-sm text-gray-600 dark:text-gray-300 bg-stone-200/50 dark:bg-gray-800/90 p-4 rounded-lg shadow-md w-full overflow-y-auto"
      >
        {#if isLoadingCGMCompliance}
          <div class="flex-1">
            <div class="text-sky-800 dark:text-sky-600 text-l">
              <h2>CGM Sensor Progress</h2>
              <div class="text-sm mb-2 text-gray-600 dark:text-gray-300 opacity-70">
                Loading sensor data...
              </div>
            </div>
            <div class="flex items-center gap-4 mt-3">
              <div class="relative w-24 h-24">
                <svg class="w-full h-full animate-pulse" viewBox="0 0 36 36">
                  <path
                    d="M18 2.0845
                      a 15.9155 15.9155 0 0 1 0 31.831
                      a 15.9155 15.9155 0 0 1 0 -31.831"
                    fill="none"
                    stroke="#E9E9E9"
                    opacity="0.75"
                    stroke-width="2"
                    transform="rotate(90 18 18)"
                  />
                  <path
                    d="M18 2.0845
                      a 15.9155 15.9155 0 0 1 0 31.831
                      a 15.9155 15.9155 0 0 1 0 -31.831"
                    fill="none"
                    stroke="#4CAF50"
                    opacity="0.5"
                    stroke-width="2"
                    stroke-dasharray="25, 100"
                    transform="rotate(90 18 18)"
                  />
                  <text
                    x="18"
                    y="19"
                    class="percentage dark:fill-white fill-black opacity-50"
                    text-anchor="middle"
                    dominant-baseline="middle"
                    style="font-size: 9px; font-weight: bold;"
                  >
                    ...
                  </text>
                </svg>
              </div>
              <div class="flex-1">
                <div class="text-xl font-semibold text-gray-400 dark:text-gray-500 animate-pulse">
                  Loading...
                </div>
                <div class="text-sm text-gray-400 dark:text-gray-500 animate-pulse">
                  Fetching sensor details
                </div>
              </div>
            </div>
          </div>
        {/if}
      </div>
    </div>
  {/if}

  {#if smbgCompliances[0] && smbgCompliances.length > 0}
    <div class="pt-3 flex-1 w-1/2">
      <div
        class="bg-stone-200/50 dark:bg-gray-800/90 p-4 rounded-lg shadow-md w-full overflow-y-auto"
      >
        <div class="flex gap-3">
          <div class="flex-1">
            <div class="text-sky-800 dark:text-sky-600 text-l">
              <h2>Latest SMBG</h2>
              <div class="text-sm text-gray-600 dark:text-gray-300 opacity-70">
                The most recent finger stick
              </div>
            </div>
          </div>
        </div>
        <div class="flex gap-3 mt-4">
          <div class="flex-1">
            <!-- svelte-ignore a11y-label-has-associated-control -->
            <label class="block text-xs text-gray-700 dark:text-gray-200 opacity-70 whitespace-nowrap truncate">Device</label>
            <div class="text-gray-900 dark:text-gray-100 whitespace-nowrap truncate">
              {#if smbgCompliances[0] && smbgCompliances[0].externalId}
                {smbgCompliances[0].externalId}
              {/if}
            </div>
          </div>
        </div>
        <div class="flex gap-3 mt-2">
          <div class="flex-1">
            <!-- svelte-ignore a11y-label-has-associated-control -->
            <label class="block text-xs text-gray-700 dark:text-gray-200 opacity-70 whitespace-nowrap truncate">Type</label>
            <div class="text-gray-900 dark:text-gray-100 whitespace-nowrap truncate">Fingerstick</div>
          </div>
          <div class="flex-1">
            <!-- svelte-ignore a11y-label-has-associated-control -->
            <label class="block text-xs text-gray-700 dark:text-gray-200 opacity-70 whitespace-nowrap truncate"
              >Timestamp (Local)</label
            >
            <div class="text-gray-900 dark:text-gray-100 whitespace-nowrap truncate">
              {#if smbgCompliances[0] && smbgCompliances[0].maxTimestamp}
                {formatShortDateTime(
                  smbgCompliances[0].maxTimestamp,
                  $selectedParticipant.timezoneCode
                )}
              {/if}
            </div>
          </div>
        </div>
      </div>
    </div>
  {:else}
    <div class="pt-3 flex-1 w-1/2">
      <div
        class="h-full flex text-sm text-gray-600 dark:text-gray-300 bg-stone-200/50 dark:bg-gray-800/90 p-4 rounded-lg shadow-md w-full overflow-y-auto"
      ></div>
    </div>
  {/if}
</div>

<div class="mx-1 flex gap-2 print:scale-90 print:origin-top-left">
  {#if dailyCGMs[0] && dailyCGMs.length > 0}
    <div class="pt-3 flex-1 w-1/2">
      <div
        class="bg-stone-200/50 dark:bg-gray-800/90 p-4 rounded-lg shadow-md w-full overflow-y-auto"
      >
        <div class="flex gap-3">
          <div class="w-full">
            <div class="text-sky-800 dark:text-sky-600 text-l">
              <h2 class="whitespace-nowrap truncate">{displayRange} Day CGM Compliance</h2>
              {#if dailyCGMs.length > 0}
                {@const latestActiveDay = dailyCGMs.find(
                  (d) => d.count > 0 && d.externalId !== 'null'
                )}
                {@const latestSensor = dailyCGMs.reduce((group, day) => {
                  // If this is the sensor we're looking for
                  if (
                    day.externalId === latestActiveDay?.externalId &&
                    day.location === latestActiveDay?.location &&
                    day.count > 0
                  ) {
                    // If group is empty or the last day in group is consecutive with this day
                    if (
                      group.length === 0 ||
                      // Check if this day is consecutive with the last day in group
                      Math.abs(
                        new Date(day.day).getTime() -
                          new Date(group[group.length - 1].day).getTime()
                      ) <=
                        24 * 60 * 60 * 1000
                    ) {
                      group.push(day);
                    }
                  }
                  return group;
                }, [])}
                {@const totalReadings = latestSensor.reduce((sum, day) => sum + day.count, 0)}
                <div class="text-sm text-gray-600 dark:text-gray-300 opacity-70 whitespace-nowrap truncate">
                  {latestSensor.length} day(s), {totalReadings} reading(s)
                </div>
              {/if}
            </div>
          </div>
        </div>

        <div class="flex">
          {#if dailyCGMChartVisible}
            {#if isLoadingCGM}
              <div class="w-full h-64 flex items-center justify-center">
                <div class="text-gray-500">Loading CGM data...</div>
              </div>
            {:else if dailyCGMs.length > 0}
              <div class="flex w-[99%]">
                {#key themeKey}
                  <DailyCGMChart data={dailyCGMs.slice(0, displayRange)} />
                {/key}
              </div>
            {/if}
          {/if}
        </div>
      </div>
    </div>
  {/if}

  {#if dailySMBGs[0] && dailySMBGs.length > 0}
    <div class="pt-3 flex-1 w-1/2">
      <div
        class="bg-stone-200/50 dark:bg-gray-800/90 p-4 rounded-lg shadow-md w-full overflow-y-auto"
      >
        <div class="flex">
          <div class="w-full">
            <div class="text-sky-800 dark:text-sky-600 text-l">
              <h2 class="whitespace-nowrap truncate">{displayRange} Day SMBG Compliance</h2>
              <div class="text-sm text-gray-600 dark:text-gray-300 opacity-70 whitespace-nowrap truncate">
                Finger sticks per day
              </div>
            </div>
          </div>
        </div>

        <div class="flex gap-3">
          {#if dailySMBGChartVisible}
            {#if isLoadingSMBG}
              <div class="w-full h-64 flex items-center justify-center">
                <div class="text-gray-500">Loading SMBG data...</div>
              </div>
            {:else if dailySMBGs.length > 0}
              <div class="flex w-[99%]">
                {#key themeKey}
                  <DailySMBGChart data={dailySMBGs.slice(0, displayRange)} />
                {/key}
              </div>
            {/if}
          {/if}
        </div>
      </div>
    </div>
  {:else}
    <div class="pt-3 flex-1 w-1/2">
      <div
        class="h-full flex text-sm text-gray-600 dark:text-gray-300 bg-stone-200/50 dark:bg-gray-800/90 p-4 rounded-lg shadow-md w-full overflow-y-auto"
      ></div>
    </div>
  {/if}
</div>

<PopupForm
  {showForm}
  editPerson={editParticipant}
      on:submit={handleEditPerson}
  on:close={() => (showForm = false)}
/>

<AuditLogModal
  showModal={showAuditLog}
  documentId={auditLogParticipant?._id}
  collection="Subject"
  title={`Audit Logs - ${auditLogParticipant?.subjectId || 'Participant'}`}
  on:close={() => (showAuditLog = false)}
/>

<SubjectFormProxyModal
  showModal={showSubjectFormProxyModal}
  subjectGUID={$selectedParticipant?._id}
  on:close={() => (showSubjectFormProxyModal = false)}
/>
